import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { MODE_LABELS } from "@/lib/student";

const fmt = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const ClassesPanel = () => {
  const [classes, setClasses] = useState([]);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState([]);

  const load = useCallback(() => api.get("/classes").then((r) => setClasses(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (selected) api.get("/teacher/results", { params: { class_id: selected.id } }).then((r) => setResults(r.data));
  }, [selected]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/classes", { name });
      setName("");
      toast.success("Classe creata");
      load();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la classe ${c.name} e i suoi risultati?`)) return;
    try {
      await api.delete(`/classes/${c.id}`);
      if (selected?.id === c.id) setSelected(null);
      toast.success("Classe eliminata");
      load();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-[280px_1fr]" data-testid="classes-panel">
      <div className="space-y-3">
        <form onSubmit={add} className="flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="es. 3A" maxLength={30} data-testid="class-name-input" className="w-full rounded-xl border-2 border-[var(--ink)] bg-white px-3 py-2 font-semibold outline-none" />
          <button disabled={!name.trim()} data-testid="class-add-button" className="ink-box-sm ink-press rounded-xl bg-[var(--sun)] px-3 disabled:opacity-50"><Plus className="h-4 w-4" /></button>
        </form>
        {classes.length === 0 && <p className="text-sm font-semibold text-neutral-500" data-testid="classes-empty">Crea la prima classe: gli alunni la sceglieranno nel quiz.</p>}
        {classes.map((c) => (
          <div key={c.id} className={`flex items-center gap-2 rounded-xl border-2 border-[var(--ink)] px-3 py-2 ${selected?.id === c.id ? "bg-[var(--ink)] text-white" : "bg-white"}`}>
            <button onClick={() => setSelected(c)} data-testid={`class-select-${c.id}`} className="flex flex-1 items-center gap-2 text-left font-extrabold"><Users className="h-4 w-4" /> {c.name}</button>
            <button onClick={() => remove(c)} data-testid={`class-delete-${c.id}`} aria-label="Elimina classe"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="ink-box-sm rounded-2xl bg-white p-5" data-testid="class-results">
        {!selected ? (
          <p className="font-semibold text-neutral-500">Seleziona una classe per vedere i risultati dei quiz.</p>
        ) : (
          <>
            <h3 className="font-display text-xl font-bold">Risultati {selected.name}</h3>
            {results.length === 0 ? (
              <p className="mt-3 text-sm font-semibold text-neutral-500" data-testid="class-results-empty">Nessun quiz svolto.</p>
            ) : (
              <table className="mt-3 w-full text-sm" data-testid="class-results-table">
                <thead><tr className="border-b-2 border-[var(--ink)] text-left"><th className="py-2">Alunno</th><th>Quiz</th><th>Materia</th><th>Punti</th><th>Data</th></tr></thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.id} className="border-b border-neutral-200">
                      <td className="py-2 font-bold">{r.student_name}</td>
                      <td>{MODE_LABELS[r.mode] || r.mode}</td>
                      <td>{r.subject || "Tutte"}</td>
                      <td className="font-extrabold">{r.score}/{r.total}</td>
                      <td className="text-neutral-500">{fmt(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
};
